import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { ComponentBase } from 't4core';

export interface InstructionSlide {
  image: string;
  video?: string;
  text?: string;
  title?: string;
  alt?: string;
  requiresPin?: boolean;
  waitingForPinText?: string;
}

@Component({
  selector: 'app-instruction-slider',
  templateUrl: './instruction-slider.component.html',
  styleUrls: ['./instruction-slider.component.css'],
  standalone: false
})
export class InstructionSliderComponent extends ComponentBase implements OnChanges {
  @Input() title: string = '';
  @Input() description: string = '';
  @Input() secondaryColor: boolean = true;
  @Input() slides: InstructionSlide[] = [];
  @Input() unlockPin: string = '';

  @Output() finalSlideReached = new EventEmitter<void>();

  public currentSlideIndex: number = 0;
  public failedVideos: { [index: number]: boolean } = {};

  private touchStartX: number | null = null;
  private touchStartY: number | null = null;
  private readonly swipeThreshold: number = 40;
  private hasReportedFinalSlide: boolean = false;

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['slides']) {
      return;
    }

    this.currentSlideIndex = 0;
    this.failedVideos = {};
    this.hasReportedFinalSlide = false;

    // A slider containing one slide is already on its final slide.
    if (this.slides?.length === 1) {
      Promise.resolve().then(() => this.reportFinalSlideReached());
    }
  }

  public get isWaitingForPin(): boolean {
    return !!this.slides?.[this.currentSlideIndex]?.requiresPin &&
      !this.unlockPin;
  }

  public get canMoveToNextSlide(): boolean {
    return !this.isLastSlide && !this.isWaitingForPin;
  }

  public get isFirstSlide(): boolean {
    return this.currentSlideIndex === 0;
  }

  public get isLastSlide(): boolean {
    return this.currentSlideIndex === this.slides.length - 1;
  }

  public nextSlide(): void {
    if (
      !this.slides?.length || this.isLastSlide || this.isWaitingForPin
    ) {
      return;
    }

    this.currentSlideIndex++;

    if (this.isLastSlide) {
      this.reportFinalSlideReached();
    }
  }

  public previousSlide(): void {
    if (!this.slides?.length || this.isFirstSlide) {
      return;
    }

    this.currentSlideIndex--;
  }

  public shouldShowVideo(index: number): boolean {
    return !!this.slides?.[index]?.video && !this.failedVideos[index];
  }

  public onVideoError(index: number): void {
    this.failedVideos[index] = true;
  }

  private reportFinalSlideReached(): void {
    if (this.hasReportedFinalSlide) {
      return;
    }

    this.hasReportedFinalSlide = true;
    this.finalSlideReached.emit();
  }

  public onTouchStart(event: TouchEvent): void {
    const touch = event.touches[0];

    if (!touch) {
      return;
    }

    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
  }

  public onTouchEnd(event: TouchEvent): void {
    if (this.touchStartX === null || this.touchStartY === null) {
      return;
    }

    const touch = event.changedTouches[0];

    if (!touch) {
      this.resetTouchPosition();
      return;
    }

    const horizontalDistance = touch.clientX - this.touchStartX;
    const verticalDistance = touch.clientY - this.touchStartY;

    this.resetTouchPosition();

    if (
      Math.abs(horizontalDistance) < this.swipeThreshold ||
      Math.abs(horizontalDistance) <= Math.abs(verticalDistance)
    ) {
      return;
    }

    if (horizontalDistance < 0) {
      this.nextSlide();
    } else {
      this.previousSlide();
    }
  }

  private resetTouchPosition(): void {
    this.touchStartX = null;
    this.touchStartY = null;
  }
}
